import { NAV_ITEMS, type NavId } from "@/data/nav";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useChat } from "@/components/ChatContext";
import { resolvePerson } from "@/lib/roster";
import { Avatar } from "@/components/ui/Avatar";
import { X, LogOut, Sparkles } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { Proposal } from "@/lib/types";
import { canSeeProposals } from "@/lib/access";
import { useOrinAdmin } from "@/hooks/useOrinAdmin";

interface Props {
  active: NavId;
  onSelect: (id: NavId) => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

function SidebarContent({ active, onSelect, onClose }: {
  active: NavId;
  onSelect: (id: NavId) => void;
  onClose?: () => void;
}) {
  const { isAdmin, user } = useAuth();
  const { openDrawer } = useChat();
  const canProposals = canSeeProposals(user?.email);
  const { isOrinAdmin } = useOrinAdmin();
  const items = NAV_ITEMS.filter((i) => {
    if (i.id === "criteria") return isAdmin;
    if (i.id === "admin") return isAdmin;
    if (i.id === "orin_admin") return isOrinAdmin;
    if (i.id === "proposals") return canProposals;
    return true;
  });

  const { data: proposals = [] } = useQuery<Proposal[]>({
    queryKey: ["proposals"],
    queryFn: async () => {
      const res = await fetch("/api/proposals", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    enabled: canProposals,
  });
  const pendingCount = proposals.filter((p) => p.status === "pending").length;
  return (
    <>
      <div className="flex items-start justify-between px-5 pb-5 pt-6">
        <div>
          <div className="text-micro uppercase tracking-[0.18em] text-sidebar-foreground/60">Fello</div>
          <div className="mt-1 text-h4 font-semibold leading-none">Felix Launch</div>
          <div className="mt-1 text-caption text-sidebar-foreground/70">Command Center</div>
        </div>
        {onClose && (
          <button onClick={onClose} className="mt-1 text-sidebar-foreground/60 hover:text-white md:hidden">
            <X size={18} />
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => { openDrawer(); onClose?.(); }}
        className="group relative mx-3 mb-4 flex items-center gap-2.5 overflow-hidden rounded-lg px-3 py-2.5 text-left text-body font-medium text-white shadow-[0_6px_18px_-8px_rgba(168,85,247,0.55)] transition-transform hover:-translate-y-[1px]"
        style={{ background: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 55%, #f97316 100%)" }}
      >
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-60 transition-opacity group-hover:opacity-90" />
        <Sparkles size={15} className="relative shrink-0 drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]" />
        <span className="relative flex-1 truncate">Ask AI</span>
      </button>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              onClick={() => { onSelect(item.id); onClose?.(); }}
              className={cn(
                "group flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-body transition-colors",
                isActive ? "bg-white/10 font-medium text-white" : "text-sidebar-foreground/70 hover:bg-white/5 hover:text-white"
              )}
            >
              <Icon size={16} className={cn(isActive ? "text-primary" : "text-sidebar-foreground/60 group-hover:text-white")} />
              <span className="flex-1 truncate">{item.label}</span>
              {item.id === "proposals" && pendingCount > 0 && (
                <span className="rounded-full bg-primary px-1.5 py-0.5 text-micro font-semibold text-white">{pendingCount}</span>
              )}
              <kbd className="hidden text-micro text-sidebar-foreground/40 group-hover:block">{item.shortcut}</kbd>
            </button>
          );
        })}
      </nav>

      {(() => {
        const me = resolvePerson(user?.email);
        const displayName = me?.name ?? user?.name ?? "Signed in";
        const displayEmail = me?.email ?? user?.email ?? "";
        return (
          <a
            href="/auth/logout"
            className="mx-3 mb-5 mt-4 flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-caption text-sidebar-foreground/80 transition-colors hover:bg-white/10 hover:text-white"
            title="Sign out"
          >
            <Avatar
              name={displayName}
              src={me?.photo}
              size="sm"
              className="ring-2 ring-white/10"
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-body font-medium">{displayName}</div>
              <div className="truncate text-micro text-sidebar-foreground/60">{displayEmail}</div>
            </div>
            <LogOut size={14} className="shrink-0 opacity-70" />
          </a>
        );
      })()}
    </>
  );
}

export function Sidebar({ active, onSelect, mobileOpen, onMobileClose }: Props) {
  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden h-full w-56 shrink-0 flex-col bg-sidebar text-sidebar-foreground md:flex">
        <SidebarContent active={active} onSelect={onSelect} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={onMobileClose} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-sidebar text-sidebar-foreground shadow-2xl">
            <SidebarContent active={active} onSelect={onSelect} onClose={onMobileClose} />
          </aside>
        </div>
      )}
    </>
  );
}
